import React, { Fragment, useEffect, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import AppPagination from "../Common/AppPagination";
import PortalMenu from "../Common/PortalMenu";
import { Row, Col, Card, Table, Badge, Form, Button, Dropdown } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const AllNotifications = () => {
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState(null);

	const [page, setPage] = usePersistentState("notif_page", 1);
	const [limit, setLimit] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [moduleFilter, setModuleFilter] = usePersistentState("notif_module", "");
	const [searchTerm, setSearchTerm] = usePersistentState("notif_search", "");

	const [selectedIds, setSelectedIds] = useState([]);

	const totalPages = Math.ceil(totalCount / limit);

	const userRole = (user?.role?.name || user?.roleName || (typeof user?.role === 'string' ? user?.role : "")).toLowerCase().trim();
	const isAdmin = userRole === "admin" || userRole === "superadmin" || userRole === "super admin";

	const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
	const userPerms = (user?.permissionNames || []).map(p => normalize(p));
	const canManageNotifications = isAdmin || userPerms.includes("managenotifications");
	const canViewSystemLog = isAdmin;

	const loadUser = async () => {
		try {
			const res = await api.get("/auth/me");
			setUser(res.data?.data);
		} catch (err) {
			console.log("User Load Error:", err);
		}
	};

	const loadAllNotifications = async () => {
		if (!user) return;

		setLoading(true);
		try {
			let url = canViewSystemLog
				? `/notifications/all?page=${page}&limit=${limit}`
				: `/notifications?page=${page}&limit=${limit}`;

			if (moduleFilter) url += `&module=${moduleFilter}`;
			
			const res = await api.get(url);

			if (res?.data?.success) {
				let data = res.data.data || [];
				if (searchTerm) {
					const lowerSearch = searchTerm.toLowerCase();
					data = data.filter(n => 
						n.message?.toLowerCase().includes(lowerSearch) || 
						n.user?.name?.toLowerCase().includes(lowerSearch) ||
						n.module?.toLowerCase().includes(lowerSearch)
					);
				}
				setNotifications(data);
				setTotalCount(res.data.totalCount || 0);
			} else {
				setNotifications([]);
				setTotalCount(0);
			}
		} catch (err) {
			console.log("Error loading notifications:", err);
			setNotifications([]);
			setTotalCount(0);
		}
		setLoading(false);
	};

	useEffect(() => {
		loadUser();
	}, []);

	useEffect(() => {
		loadAllNotifications();
	}, [user, page, limit, moduleFilter]);

	const handleDelete = async (id) => {
		const confirm = await swal({
			title: "Are you sure?",
			text: "Once deleted, this notification log cannot be recovered.",
			icon: "warning",
			buttons: ["Cancel", "Yes, Delete"],
			dangerMode: true,
		});

		if (confirm) {
			try {
				const res = await api.delete(`/notifications/${id}`);
				if (res.data.success) {
					swal("Deleted!", "Notification has been removed.", "success");
					loadAllNotifications();
				}
			} catch (err) {
				console.log("Delete error:", err);
				swal("Error", "Failed to delete notification.", "error");
			}
		}
	};

	const handleBulkDelete = async () => {
		if (selectedIds.length === 0) return;

		const confirm = await swal({
			title: "Delete selected logs?",
			text: `You are about to delete ${selectedIds.length} notification entries.`,
			icon: "warning",
			buttons: ["Cancel", "Yes, Delete All"],
			dangerMode: true,
		});

		if (confirm) {
			try {
				const res = await api.post("/notifications/bulk-delete", { ids: selectedIds });
				if (res.data.success) {
					swal("Deleted!", res.data.message || "Selected notifications removed.", "success");
					setSelectedIds([]);
					loadAllNotifications();
				}
			} catch (err) {
				console.log("Bulk delete error:", err);
				swal("Error", "Failed to delete notifications.", "error");
			}
		}
	};

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			setSelectedIds(notifications.map(n => n._id));
		} else {
			setSelectedIds([]);
		}
	};

	const handleSelectOne = (id) => {
		if (selectedIds.includes(id)) {
			setSelectedIds(selectedIds.filter(i => i !== id));
		} else {
			setSelectedIds([...selectedIds, id]);
		}
	};

	const formatDateParts = (d) => {
		if (!d) return { date: "—", time: "" };
		const dateObj = new Date(d);
		return {
			date: dateObj.toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' }),
			time: dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
		};
	};

	const modules = [
		"WorkOrder", "Billing", "Delivery", "Indent", "MIR", 
		"JMR", "TC", "Handover", "DPR", "Project", "Client", "Vendor", "User"
	];

	const filterMeaningfulChanges = (changes) => {
		if (!Array.isArray(changes)) return [];
		return changes.filter((c) => {
			if (!c || !c.field) return false;
			const f = c.field.toLowerCase().trim();
			// Filter out internal junk fields
			if (["doc", "_doc", "id", "_id", "schema", "filecache", "clientemailsent", "clientlastemailsentat"].includes(f)) return false;
			if ((c.oldValue === "None" || c.oldValue === "null" || !c.oldValue) && (c.newValue === "Updated" || c.newValue === "null" || !c.newValue)) {
				return false;
			}
			return true;
		});
	};

	const parseActivity = (n) => {
		const rawMsg = n.message || "";
		const moduleName = n.module || "System";
		const action = (n.action || "").toLowerCase();

		let docId = null;
		const quoteMatch = rawMsg.match(/"([^"]+)"/);
		if (quoteMatch && quoteMatch[1]) {
			const candidate = quoteMatch[1].trim();
			if (!/unknown item/i.test(candidate) && !/^ID:\s*[a-f0-9]+$/i.test(candidate)) {
				docId = candidate;
			}
		}

		if (!docId) {
			const colonMatch = rawMsg.match(new RegExp(`${moduleName}[:\\s]+([A-Za-z0-9/_-]+)`, "i"));
			if (colonMatch && colonMatch[1]) {
				const candidate = colonMatch[1].trim();
				if (!["changed", "doc", "null", "undefined", "ID", "Unknown", "status", "created", "updated", "deleted", "record"].includes(candidate.toLowerCase())) {
					docId = candidate;
				}
			}
		}

		if (!docId) {
			const hashMatch = rawMsg.match(/#\s*([A-Za-z0-9/_-]+)/);
			if (hashMatch && hashMatch[1]) {
				docId = hashMatch[1].trim();
			}
		}

		// Extract context metadata from (WO: ..., Project: ..., Client: ..., Vendor: ...)
		const contexts = [];
		const parenMatch = rawMsg.match(/\(([^)]+)\)/);
		if (parenMatch && parenMatch[1]) {
			const inside = parenMatch[1];
			inside.split(",").forEach(part => {
				const colonIdx = part.indexOf(":");
				if (colonIdx !== -1) {
					const label = part.slice(0, colonIdx).trim();
					const value = part.slice(colonIdx + 1).trim();
					if (label && value) {
						contexts.push({ label, value });
					}
				} else if (part.trim()) {
					contexts.push({ label: "", value: part.trim() });
				}
			});
		}

		let title = "";
		if (docId) {
			const cleanDocId = docId.replace(new RegExp(`^(?:${moduleName}|Item)\\s*#?`, "i"), "").trim();
			title = `${moduleName} #${(cleanDocId || docId).replace(/^#/, "")}`;
		} else {
			title = `${moduleName} Record`;
		}

		let subtitle = "";
		if (action === "create") {
			subtitle = `New ${moduleName} created`;
		} else if (action === "delete") {
			subtitle = `${moduleName} removed`;
		} else if (action === "update") {
			if (/status/i.test(rawMsg)) {
				const afterStatus = rawMsg.split(/status(?:\s+of)?/i)[1];
				if (afterStatus) {
					// Clean up the status transition text
					const transition = afterStatus.replace(/^[^a-zA-Z0-9]+/, "").replace(/\s*\(.*?\)\s*/g, " ").trim();
					subtitle = `Status: ${transition}`;
				} else {
					subtitle = "Status updated";
				}
			} else if (/payment|paid|deduction/i.test(rawMsg)) {
				subtitle = "Payment Advice & Deductions updated";
			} else if (/changed\s+/i.test(rawMsg)) {
				const changedPart = rawMsg.split(/changed\s+/i)[1];
				subtitle = `Changed: ${changedPart ? changedPart.replace(/\s*\(.*?\)\s*/g, " ").trim() : "Details modified"}`;
			} else if (/doc|file|attachment/i.test(rawMsg)) {
				subtitle = "Document & file attachments updated";
			} else {
				subtitle = "Record details modified";
			}
		} else {
			subtitle = rawMsg.replace(/^[A-Za-z0-9\s._-]+:\s*/i, "").trim() || rawMsg;
		}

		return { title, subtitle, docId, contexts };
	};

	const getActionBadge = (action) => {
		const act = (action || "").toLowerCase();
		switch (act) {
			case "create":
				return <Badge bg="success" className="light"><i className="fas fa-plus-circle me-1"></i>Created</Badge>;
			case "update":
				return <Badge bg="info" className="light"><i className="fas fa-edit me-1"></i>Updated</Badge>;
			case "delete":
				return <Badge bg="danger" className="light"><i className="fas fa-times-circle me-1"></i>Deleted</Badge>;
			default:
				return <Badge bg="secondary" className="light">{action || "Activity"}</Badge>;
		}
	};

	const getModuleBadgeColor = (mod) => {
		const m = (mod || "").toLowerCase();
		switch (m) {
			case "workorder":
			case "project":
			case "client":
				return "primary";
			case "billing":
			case "purchaseorder":
			case "delivery":
			case "mir":
				return "warning";
			case "user":
			case "users":
			case "dpr":
				return "info";
			default:
				return "secondary";
		}
	};

	const svgMenu = (
		<svg width="20px" height="20px" viewBox="0 0 24 24">
			<circle fill="#000000" cx="5" cy="12" r="2"></circle>
			<circle fill="#000000" cx="12" cy="12" r="2"></circle>
			<circle fill="#000000" cx="19" cy="12" r="2"></circle>
		</svg>
	);

	return (
		<Fragment>
			<PageTitle 
				activeMenu={canViewSystemLog ? "System Audit Log" : "Notifications"} 
				motherMenu="Dashboard" 
			/>

			<Row>
				<Col lg={12}>
					<Card>
						<Card.Header className="d-flex justify-content-between align-items-center">
							<Card.Title>
								{canViewSystemLog ? "System Audit Log & Activity Trail" : "My Notifications"}
							</Card.Title>
							<div className="d-flex align-items-center gap-2">
								{canManageNotifications && selectedIds.length > 0 && (
									<Button
										variant="danger"
										size="sm"
										onClick={handleBulkDelete}
									>
										<i className="fas fa-trash-alt me-2"></i>Delete Selected ({selectedIds.length})
									</Button>
								)}
								<Button
									variant="light"
									size="sm"
									onClick={loadAllNotifications}
									disabled={loading}
								>
									<i className={`fas fa-sync-alt me-2 ${loading ? "fa-spin" : ""}`}></i>Refresh
								</Button>
							</div>
						</Card.Header>

						{/* Filter bar */}
						<div className="d-flex flex-wrap align-items-end gap-3 px-4 py-3 border-bottom">
							<div className="d-flex flex-column flex-grow-1" style={{ minWidth: "220px", maxWidth: "360px" }}>
								<label className="text-muted mb-1 fs-12 font-weight-bold">Search Notifications</label>
								<input
									type="text"
									className="form-control form-control-sm"
									placeholder="Search activity, document #, user..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									onKeyDown={(e) => e.key === "Enter" && loadAllNotifications()}
								/>
							</div>
							<div className="d-flex flex-column" style={{ width: "180px" }}>
								<label className="text-muted mb-1 fs-12 font-weight-bold">Module</label>
								<select
									className="form-select form-select-sm"
									value={moduleFilter}
									onChange={(e) => {
										setModuleFilter(e.target.value);
										setPage(1);
									}}
								>
									<option value="">All Modules</option>
									{modules.map((m) => (
										<option key={m} value={m}>{m}</option>
									))}
								</select>
							</div>
							<div className="ms-auto d-flex flex-column" style={{ width: "80px" }}>
								<label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
								<select
									className="form-select form-select-sm"
									value={limit}
									onChange={(e) => {
										setLimit(Number(e.target.value));
										setPage(1);
									}}
								>
									<option value={10}>10</option>
									<option value={20}>20</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</div>
						</div>

						<Card.Body>
							{loading ? (
								<div className="text-center py-5">
									<div className="spinner-border text-primary" role="status"></div>
									<p className="mt-3">Loading notifications...</p>
								</div>
							) : notifications.length === 0 ? (
								<div className="text-center py-5">
									<i className="fas fa-bell-slash fa-3x text-muted mb-3"></i>
									<p className="text-muted">No notifications found.</p>
									{(searchTerm || moduleFilter) && (
										<Button 
											variant="light" 
											size="sm" 
											onClick={() => {
												setSearchTerm("");
												setModuleFilter("");
												setPage(1);
											}}
										>
											Clear Filters
										</Button>
									)}
								</div>
							) : (
								<div className="table-responsive">
									<Table hover className="align-middle">
										<thead className="table-light">
											<tr>
												{canManageNotifications && (
													<th style={{ width: "40px" }} className="text-center">
														<Form.Check
															type="checkbox"
															checked={notifications.length > 0 && selectedIds.length === notifications.length}
															onChange={handleSelectAll}
														/>
													</th>
												)}
												<th style={{ width: "50px" }} className="text-center">#</th>
												<th style={{ width: "130px" }}>Module</th>
												<th style={{ width: "110px" }}>Action</th>
												<th style={{ minWidth: "240px" }}>Activity</th>
												<th style={{ minWidth: "260px" }}>Changes / Details</th>
												<th style={{ width: "180px" }}>Performed By</th>
												<th style={{ width: "150px" }} className="text-center">Date & Time</th>
												{canManageNotifications && (
													<th style={{ width: "70px" }} className="text-center">Action</th>
												)}
											</tr>
										</thead>

										<tbody>
											{notifications.map((n, index) => {
												const validChanges = filterMeaningfulChanges(n.changes);
												const { title, subtitle, contexts } = parseActivity(n);
												const timeParts = formatDateParts(n.createdAt);
												const performerName = n.performer?.name || "System";
												const performerEmail = n.performer?.email || "";

												return (
													<tr key={n._id}>
														{canManageNotifications && (
															<td className="text-center">
																<Form.Check
																	type="checkbox"
																	checked={selectedIds.includes(n._id)}
																	onChange={() => handleSelectOne(n._id)}
																/>
															</td>
														)}
														<td className="text-center text-muted">
															{(page - 1) * limit + index + 1}
														</td>
														<td>
															<Badge bg={getModuleBadgeColor(n.module)} className="light">
																{n.module || "System"}
															</Badge>
														</td>
														<td>
															{getActionBadge(n.action)}
														</td>
														<td>
															<div className="d-flex align-items-center flex-wrap gap-1">
																<span className="fw-bold text-dark">{title}</span>
																{contexts && contexts.length > 0 && contexts.map((ctx, cIdx) => (
																	<span 
																		key={cIdx} 
																		className="badge bg-light text-secondary border font-weight-normal py-1 px-1.5"
																		style={{ fontSize: "11px" }}
																	>
																		{ctx.label && <strong className="text-primary me-1">{ctx.label}:</strong>}
																		{ctx.value}
																	</span>
																))}
															</div>
															<div className="text-muted small mt-0.5">{subtitle}</div>
														</td>
														<td>
															{validChanges.length > 0 ? (
																<div className="d-flex flex-column gap-1">
																	{validChanges.map((change, i) => (
																		<div key={i} className="small">
																			<span className="text-muted font-weight-bold me-1">
																				{change.field?.replace(/([A-Z])/g, ' $1').trim()}:
																			</span>
																			<span className="text-danger text-decoration-line-through me-1">
																				{change.oldValue || "None"}
																			</span>
																			<i className="fas fa-arrow-right text-muted mx-1" style={{ fontSize: "10px" }}></i>
																			<span className="text-success font-weight-bold">
																				{change.newValue || "None"}
																			</span>
																		</div>
																	))}
																</div>
															) : n.action === "create" ? (
																<span className="text-success small">
																	<i className="fas fa-check-circle me-1"></i>Initial Record Created
																</span>
															) : n.action === "delete" ? (
																<span className="text-danger small">
																	<i className="fas fa-trash-alt me-1"></i>Record Removed
																</span>
															) : (
																<span className="text-muted small">
																	<i className="fas fa-info-circle me-1"></i>{n.message || "Record updated"}
																</span>
															)}
														</td>
														<td>
															<div className="fw-bold text-dark">{performerName}</div>
															{performerEmail && (
																<div className="text-muted small">{performerEmail}</div>
															)}
														</td>
														<td className="text-center">
															<div>{timeParts.date}</div>
															<div className="text-muted small">{timeParts.time}</div>
														</td>
														{canManageNotifications && (
															<td className="text-center" style={{ overflow: 'visible' }}>
																<Dropdown drop="start i-false" className="ms-auto text-center">
																	<Dropdown.Toggle
																		as="div"
																		className="btn-link i-false cursor-pointer p-0"
																		style={{ cursor: "pointer" }}
																		title="Click for options"
																	>
																		{svgMenu}
																	</Dropdown.Toggle>
																	<Dropdown.Menu align="end" as={PortalMenu}>
																		<Dropdown.Item
																			onClick={() => handleDelete(n._id)}
																			className="text-danger"
																		>
																			<i className="fa fa-trash me-2"></i> Delete
																		</Dropdown.Item>
																	</Dropdown.Menu>
																</Dropdown>
															</td>
														)}
													</tr>
												);
											})}
										</tbody>
									</Table>
								</div>
							)}

							<AppPagination
								currentPage={page}
								totalPages={totalPages}
								totalCount={totalCount}
								itemsLength={notifications.length}
								limit={limit}
								onPageChange={setPage}
							/>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</Fragment>
	);
};

export default AllNotifications;
